import { quotationRows, profileBom, glassBom, infillBom, hardwareOrder, cutPlan, barsByProfile, cutLabel } from "@/lib/bom";

const HEAD = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0E7490" } };
const BAND = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } };
const thin = { style: "thin", color: { argb: "FFCBD5E1" } };
const BORDER = { top: thin, left: thin, bottom: thin, right: thin };

function sheetTitle(ws, title, tb, span) {
  ws.mergeCells(1, 1, 1, span);
  ws.getCell(1, 1).value = `${tb.project_name || "Project"} — ${title}`;
  ws.getCell(1, 1).font = { bold: true, size: 14 };
  ws.getCell(2, 1).value = [tb.client && `Client: ${tb.client}`, tb.drawing_no && `Ref: ${tb.drawing_no}`, `Date: ${tb.date || new Date().toISOString().slice(0, 10)}`].filter(Boolean).join("   ·   ");
  ws.getCell(2, 1).font = { size: 9, color: { argb: "FF555555" } };
}

function table(ws, startRow, headers, rows, widths) {
  const h = ws.getRow(startRow);
  headers.forEach((t, i) => { const c = h.getCell(i + 1); c.value = t; c.fill = HEAD; c.font = { bold: true, color: { argb: "FFFFFFFF" } }; c.border = BORDER; });
  rows.forEach((r, j) => {
    const row = ws.getRow(startRow + 1 + j);
    r.forEach((v, i) => { const c = row.getCell(i + 1); c.value = v; c.border = BORDER; });
  });
  if (widths) widths.forEach((w, i) => { ws.getColumn(i + 1).width = Math.max(ws.getColumn(i + 1).width || 0, w); });
  return startRow + rows.length + 1;
}

function quotation(wb, items, tb, s) {
  const ws = wb.addWorksheet("Quotation");
  sheetTitle(ws, "Quotation (quantities — enter rates)", tb, 13);
  const rows = quotationRows(items), r0 = 4;
  const end = table(ws, r0, ["Sr", "Tag", "Description", "Typology", "System", "Location", "W mm", "H mm", "Area/unit m²", "Qty", "Total m²", "Rate / m²", "Amount"],
    rows.map((q, j) => { const R = r0 + 1 + j; return [q.sr, q.tag, q.desc, q.typology, q.system, q.location, q.w, q.h, q.area, q.qty, q.total, null, { formula: `K${R}*L${R}` }]; }),
    [5, 9, 26, 20, 34, 14, 8, 8, 12, 6, 10, 11, 14]);
  for (let R = r0 + 1; R < end; R++) ws.getCell(`L${R}`).fill = BAND;
  const S = end + 1;
  const put = (r, label, v, bold) => { ws.getCell(`L${r}`).value = label; ws.getCell(`M${r}`).value = v; ws.getCell(`L${r}`).font = { bold }; ws.getCell(`M${r}`).font = { bold }; ws.getCell(`M${r}`).numFmt = "#,##0.00"; };
  ws.getCell(`J${S}`).value = "Total m²"; ws.getCell(`K${S}`).value = { formula: `SUM(K${r0 + 1}:K${end - 1})` };
  put(S, "Subtotal", { formula: `SUM(M${r0 + 1}:M${end - 1})` });
  put(S + 1, "GST %", s.gst);
  ws.getCell(`M${S + 1}`).numFmt = "0.00"; ws.getCell(`M${S + 1}`).fill = BAND;
  put(S + 2, "GST amount", { formula: `M${S}*M${S + 1}/100` });
  put(S + 3, "Grand total", { formula: `M${S}+M${S + 2}` }, true);
  ws.getCell(`A${S + 5}`).value = "Rates are left blank (quantities only). Enter a rate per m² in the blue cells — amounts, GST and totals calculate automatically.";
  ws.getCell(`A${S + 5}`).font = { italic: true, size: 9, color: { argb: "FF555555" } };
}

function bom(wb, items, tb, plan) {
  const ws = wb.addWorksheet("BOM");
  sheetTitle(ws, "Bill of materials", tb, 9);
  ws.getCell("A4").value = "PROFILES"; ws.getCell("A4").font = { bold: true };
  const bars = barsByProfile(plan), prof = profileBom(items), r0 = 5;
  let end = table(ws, r0, ["Profile / member", "Code", "System", "Pieces", "Total length m", `Bars (${plan.groups.length ? "cut plan" : "—"})`, "kg/m", "Weight kg", "Used in"],
    prof.map((p, j) => { const R = r0 + 1 + j; return [p.name, p.code || "—", p.system, p.count, p.m, bars.get(p.key) || 0, null, { formula: `E${R}*G${R}` }, p.tags]; }),
    [26, 12, 36, 8, 14, 12, 8, 11, 40]);
  for (let R = r0 + 1; R < end; R++) ws.getCell(`G${R}`).fill = BAND;
  ws.getCell(`G${end}`).value = "Total kg"; ws.getCell(`H${end}`).value = { formula: `SUM(H${r0 + 1}:H${end - 1})` };
  ws.getCell(`G${end}`).font = { bold: true };
  const g0 = end + 3;
  ws.getCell(`A${g0 - 1}`).value = "GLASS"; ws.getCell(`A${g0 - 1}`).font = { bold: true };
  end = table(ws, g0, ["Glass specification", "", "", "Panes", "Area m²", "", "", "", "Used in"], glassBom(items).map((g) => [g.spec, "", "", g.panes, g.area, "", "", "", g.tags]));
  const inf = infillBom(items);
  if (inf.rows.length) {
    const i0 = end + 3, rows = [...inf.rows.map((r) => [r.mat.label, r.spec, "", r.panes, r.area, "", "", "", r.tags]), ...inf.totals.map((t) => [`Total ${t.mat.label}`, "", "", t.panes, t.area, "", "", "", ""])];
    ws.getCell(`A${i0 - 1}`).value = "INFILL PANELS (NON-GLASS · SIZES INCL. BITE)"; ws.getCell(`A${i0 - 1}`).font = { bold: true };
    end = table(ws, i0, ["Material", "Specification", "", "Panels", "Area m²", "", "", "", "Used in"], rows);
    [...inf.rows.map((r) => r.mat), ...inf.totals.map((t) => t.mat)].forEach((m, j) => {
      ws.getCell(`A${i0 + 1 + j}`).fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${m.print.slice(1)}` } };
      if (j >= inf.rows.length) ws.getRow(i0 + 1 + j).font = { bold: true };
    });
  }
  ws.getCell(`A${end + 1}`).value = "Enter kg/m from the IWIN-S / YKK AP profile catalogue in the blue cells to get profile weights.";
  ws.getCell(`A${end + 1}`).font = { italic: true, size: 9, color: { argb: "FF555555" } };
}

function cuts(wb, tb, plan, s) {
  const ws = wb.addWorksheet("Cut plan");
  sheetTitle(ws, "Bar cutting plan", tb, 8);
  ws.getCell("A3").value = `Bar ${s.bar} mm · saw cut ${s.kerf} mm · end trim ${s.trim} mm each end · ${s.mode === "project" ? "optimised across the whole project" : "optimised item by item"} · ${plan.bars} bars · waste ${plan.waste}%`;
  const rows = plan.groups.flatMap((g) => g.patterns.map((p, i) => [g.tag || "All items", `${g.name}${g.code ? ` (${g.code})` : ""}`, g.system, i + 1, p.n, p.cuts.map(cutLabel).join("  +  "), p.used, p.offcut]));
  const end = table(ws, 5, ["Item", "Profile", "System", "Pattern", "× bars", "Cuts (length mm · mark · item)", "Cut total mm", "Offcut mm"], rows, [10, 28, 34, 8, 8, 80, 12, 10]);
  if (plan.oversize.length) ws.getCell(`A${end + 1}`).value = `Longer than the usable bar (order cut-to-size or joint): ${plan.oversize.map(cutLabel).join(", ")}`;
}

function hardware(wb, items, tb) {
  const ws = wb.addWorksheet("Hardware order");
  sheetTitle(ws, "Hardware & accessories order", tb, 6);
  let r = 4;
  hardwareOrder(items).forEach((g) => {
    ws.mergeCells(r, 1, r, 6);
    ws.getCell(r, 1).value = g.group; ws.getCell(r, 1).font = { bold: true }; ws.getCell(r, 1).fill = BAND;
    r = table(ws, r + 1, ["Part", "Specification", "Total qty", "Unit", "Items (tag × qty)", "Ordered"], g.rows.map((h) => [h.name, h.spec, h.qty, h.unit, h.tags, ""]), [30, 42, 10, 7, 44, 10]) + 1;
  });
}

export async function exportXlsx(items, tb, settings) {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "AluDraft.ai";
  const plan = cutPlan(items, settings);
  quotation(wb, items, tb, settings);
  bom(wb, items, tb, plan);
  cuts(wb, tb, plan, settings);
  hardware(wb, items, tb);
  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  a.download = `${(tb.project_name || "project").replace(/\s+/g, "_")}_BOM.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
